// Designates one building in a tactical zone as harbouring a wild survivor the
// player can recruit mid-run (see the recruit flow in tactical-view.mjs /
// tactical-turn.mjs). Modeled on building-scavenge.mjs: a pure, seeded overlay
// keyed by the same stable buildingKey, so the same zone + worldSeed always
// flags the same building (the strategy screen and the supply run you drop into agree).
//
// A survivor building carries no supply pool — it offers the recruit menu only,
// never scavenge (the two systems stay disjoint by construction here, since the
// host excludes promising/supply buildings via excludeIds). The full survivor
// identity (name + profession) is baked at seed time so the in-run companion
// label and the roster entry banked on run completion are the same person.

import { buildingKey } from "./building-scavenge.mjs";
import { SURVIVOR_PROFESSION_IDS } from "./professions-v4.mjs";

// Names for survivors found in the field — kept distinct from the starting
// roster so a recruit reads as someone new. Collisions with living survivors
// are avoided when possible (see excludeNames), but the survivor id is the real
// unique key, so a duplicate name is only cosmetic.
export const WILD_SURVIVOR_NAMES = [
  "Rowan", "Sasha", "Miles", "Tessa", "Dario", "Nadia", "Emory", "Priya",
  "Cass", "Lena", "Bruno", "Iris", "Theo", "Marisol", "Kade", "Noor",
  "Silas", "June", "Rafe", "Odette", "Hollis", "Ximena", "Dov", "Paloma",
];

// --- Deterministic RNG (string seed -> mulberry32) ----------------------
// Mirrors building-scavenge.mjs so the two overlays share seeding behaviour.

function hashStringToSeed(value) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function mulberry32(seedInt) {
  let state = seedInt >>> 0;
  return function next() {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Deterministic Fisher-Yates so the pick is stable for a seed but not tied to
// input order.
function shuffled(items, rng) {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function pickName(rng, excludeNames) {
  const excluded = excludeNames instanceof Set ? excludeNames : new Set(excludeNames);
  const available = WILD_SURVIVOR_NAMES.filter((name) => !excluded.has(name));
  const pool = available.length ? available : WILD_SURVIVOR_NAMES;
  return pool[Math.floor(rng() * pool.length)];
}

// --- Public API ---------------------------------------------------------

// Choose exactly one survivor building for a zone. Returns a descriptor:
//   { buildingId, name, backgroundId }
// or null when there are no eligible buildings. `excludeIds` skips buildings
// that are already spoken for (e.g. forced-promising supply buildings) so a
// survivor building is never also a supply building. `excludeNames` biases the
// name pick away from names already on the roster.
export function assignSurvivorBuilding({
  buildings = [],
  zoneId = "",
  worldSeed = "",
  excludeIds = [],
  excludeNames = [],
  rng,
} = {}) {
  const roll = rng ?? mulberry32(hashStringToSeed(`${worldSeed}:${zoneId}:survivor`));
  const exclude = excludeIds instanceof Set ? excludeIds : new Set(excludeIds);

  const candidates = buildings
    .map((feature, index) => ({ id: buildingKey(feature, index), feature }))
    .filter((entry) => !exclude.has(entry.id));
  if (candidates.length === 0) return null;

  const pick = shuffled(candidates, roll)[0];
  const name = pickName(roll, excludeNames);
  const backgroundId = SURVIVOR_PROFESSION_IDS[Math.floor(roll() * SURVIVOR_PROFESSION_IDS.length)];

  return { buildingId: String(pick.id), name, backgroundId };
}
